import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Rocket,
  Clock,
  Target,
  Users,
  ChevronDown,
  ChevronRight,
  Layers,
  Settings,
  BarChart3,
  Lightbulb,
  Zap,
  ArrowRight,
  XCircle,
  Timer,
  Shuffle,
  RefreshCw,
  CheckCircle2,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui';

interface AccordionSectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function AccordionSection({ icon, title, children, defaultOpen = false }: AccordionSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className="
        border-b border-border-default/50
        last:border-b-0
      "
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          -mx-2 flex w-full items-center gap-3 rounded-lg px-2 py-4 text-left transition-colors
          hover:bg-bg-muted/50
        "
      >
        <span className="text-action-primary">{icon}</span>
        <span className="flex-1 font-semibold text-text-primary">{title}</span>
        {isOpen ? (
          <ChevronDown className="size-5 text-text-muted" />
        ) : (
          <ChevronRight className="size-5 text-text-muted" />
        )}
      </button>
      {isOpen && <div className="space-y-3 pr-2 pb-4 pl-9 text-text-secondary">{children}</div>}
    </div>
  );
}

function Highlight({ children }: { children: React.ReactNode }) {
  return <span className="font-medium text-text-primary">{children}</span>;
}

function CodeText({ children }: { children: React.ReactNode }) {
  return <code className="rounded-sm bg-bg-muted px-1.5 py-0.5 font-mono text-sm text-action-primary">{children}</code>;
}

export function HowToPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-12 pb-12">
      {/* Hero Section */}
      <div className="space-y-6 py-8 text-center">
        <h1
          className="
            text-4xl font-bold text-text-primary
            md:text-5xl
          "
        >
          Ship on Time,{' '}
          <span className="bg-linear-to-r from-primary-500 to-accent-cyan bg-clip-text text-transparent">
            Every Time
          </span>
        </h1>
        <p className="mx-auto max-w-2xl text-xl/relaxed text-text-secondary">
          Forecast delivery dates in seconds—not days. Make decisions based on what you can actually control.
        </p>
      </div>

      {/* The Problem Section */}
      <Card>
        <CardContent className="space-y-6 p-8">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-accent-rose/10 p-2">
              <Clock className="size-6 text-accent-rose" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">The Planning Problem</h2>
          </div>

          <p className="leading-relaxed text-text-secondary">
            In most organizations, answering <Highlight>"when will this be done?"</Highlight> follows a painful,
            expensive ritual:
          </p>

          {/* Pain Points Grid */}
          <div
            className="
              grid gap-4
              sm:grid-cols-2
            "
          >
            <div className="flex items-start gap-3 rounded-lg border border-accent-rose/20 bg-accent-rose/5 p-4">
              <Timer className="mt-0.5 size-5 shrink-0 text-accent-rose" />
              <div>
                <p className="font-medium text-text-primary">Slow & expensive</p>
                <p className="text-sm text-text-secondary">
                  Decompose into tasks, estimate each one, synchronize across teams, sum it all up
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-accent-rose/20 bg-accent-rose/5 p-4">
              <Users className="mt-0.5 size-5 shrink-0 text-accent-rose" />
              <div>
                <p className="font-medium text-text-primary">Requires everyone</p>
                <p className="text-sm text-text-secondary">
                  Even simple decisions require pulling in developers, scheduling meetings, waiting days
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-accent-rose/20 bg-accent-rose/5 p-4">
              <Shuffle className="mt-0.5 size-5 shrink-0 text-accent-rose" />
              <div>
                <p className="font-medium text-text-primary">Constantly disrupted</p>
                <p className="text-sm text-text-secondary">
                  Other work interrupts. Priorities shift. Multiple things compete for attention
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-accent-rose/20 bg-accent-rose/5 p-4">
              <XCircle className="mt-0.5 size-5 shrink-0 text-accent-rose" />
              <div>
                <p className="font-medium text-text-primary">Still wrong</p>
                <p className="text-sm text-text-secondary">
                  After all that effort, estimates still break. You miss dates you thought were "carefully planned"
                </p>
              </div>
            </div>
          </div>

          {/* Punchline */}
          <div className="border-l-4 border-accent-rose py-2 pl-4">
            <p className="font-semibold text-text-primary">
              The problem isn't bad estimating—it's trying to control what you can't.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* The Solution Section */}
      <Card>
        <CardContent className="space-y-6 p-8">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-feedback-success/10 p-2">
              <Zap className="size-6 text-feedback-success" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">A Different Approach</h2>
          </div>

          <p className="leading-relaxed text-text-secondary">
            Instead of managing dates—which you have no direct control over—
            <Highlight>Shipcast focuses on the decisions you can actually make:</Highlight>
          </p>

          {/* Controllable Levers */}
          <div
            className="
              grid gap-4
              sm:grid-cols-3
            "
          >
            <div className="space-y-2 rounded-lg border border-feedback-success/20 bg-feedback-success/5 p-4">
              <div className="flex items-center gap-2">
                <Target className="size-5 text-feedback-success" />
                <p className="font-semibold text-text-primary">WIP Limits</p>
              </div>
              <p className="text-sm text-text-secondary">
                How many things should we work on at once? Teams decide based on capacity.
              </p>
            </div>
            <div className="space-y-2 rounded-lg border border-feedback-success/20 bg-feedback-success/5 p-4">
              <div className="flex items-center gap-2">
                <Layers className="size-5 text-feedback-success" />
                <p className="font-semibold text-text-primary">Priority Order</p>
              </div>
              <p className="text-sm text-text-secondary">
                What gets done first? Product owners decide the sequence that matters.
              </p>
            </div>
            <div className="space-y-2 rounded-lg border border-feedback-success/20 bg-feedback-success/5 p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="size-5 text-feedback-success" />
                <p className="font-semibold text-text-primary">Scope Ranges</p>
              </div>
              <p className="text-sm text-text-secondary">
                How uncertain is the work? Express it as a range (30–120 items), not a guess.
              </p>
            </div>
          </div>

          {/* The Loop */}
          <div className="flex items-start gap-4 rounded-lg border border-border-default bg-bg-muted/50 p-4">
            <RefreshCw className="mt-1 size-6 shrink-0 text-action-primary" />
            <div className="space-y-2">
              <p className="font-medium text-text-primary">The feedback loop that changes everything</p>
              <p className="text-sm text-text-secondary">
                Feed these inputs into a Monte Carlo simulation → get a probabilistic forecast{' '}
                <Highlight>in seconds</Highlight> → ask yourself:{' '}
                <em className="text-text-primary">Do I like this date?</em> → if not, adjust inputs—reduce scope,
                increase capacity, reprioritize—and see the impact <Highlight>immediately</Highlight>.
              </p>
            </div>
          </div>

          {/* Value Statement */}
          <div
            className="
              relative rounded-xl border border-action-primary/20 bg-linear-to-r from-action-primary/10
              to-accent-cyan/10 p-6
            "
          >
            <div className="flex items-start gap-4">
              <CheckCircle2 className="size-8 shrink-0 text-action-primary" />
              <div className="space-y-1">
                <p className="text-lg font-bold text-text-primary">
                  What's costly and inaccurate becomes fast and actionable
                </p>
                <p className="text-text-secondary">
                  Shipcast turns planning from a weeks-long ritual into a real-time conversation based on levers you can
                  actually pull.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How It Works - 3 Steps */}
      <div className="space-y-6">
        <h2 className="text-center text-2xl font-bold text-text-primary">How It Works</h2>

        <div
          className="
            grid gap-6
            md:grid-cols-3
          "
        >
          <Card>
            <CardContent className="space-y-4 p-6 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-action-primary/10">
                <span className="text-xl font-bold text-action-primary">1</span>
              </div>
              <h3 className="font-semibold text-text-primary">Set Up Your Teams</h3>
              <p className="text-sm text-text-secondary">
                Add teams with their throughput history—how many items they complete per day. Set WIP limits to match
                how they actually work.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 p-6 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-action-primary/10">
                <span className="text-xl font-bold text-action-primary">2</span>
              </div>
              <h3 className="font-semibold text-text-primary">Add Your Work</h3>
              <p className="text-sm text-text-secondary">
                Create deliverables with estimated sizes. Don't know the exact scope? Use min/max ranges to capture
                uncertainty honestly.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 p-6 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-action-primary/10">
                <span className="text-xl font-bold text-action-primary">3</span>
              </div>
              <h3 className="font-semibold text-text-primary">Run & Explore</h3>
              <p className="text-sm text-text-secondary">
                Run simulations to see delivery forecasts with confidence levels. Adjust inputs instantly to explore
                "what if" scenarios.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reference Guide Accordion */}
      <Card>
        <CardContent className="space-y-2 p-6">
          <h2 className="mb-4 text-xl font-bold text-text-primary">Reference Guide</h2>

          <AccordionSection icon={<Users className="size-5" />} title="Teams" defaultOpen>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <Highlight>Throughput History:</Highlight> Comma-separated daily values from past data (e.g.,{' '}
                <CodeText>5, 6, 7, 8, 5, 4</CodeText>)
              </li>
              <li>
                <Highlight>Throughput Scale:</Highlight> What-if multiplier—100% baseline, 120% = 20% faster
              </li>
              <li>
                <Highlight>WIP Limit:</Highlight> Max concurrent items. Lower = more predictable, potentially slower
              </li>
              <li>Drag team tabs to reorder priority</li>
            </ul>
          </AccordionSection>

          <AccordionSection icon={<Layers className="size-5" />} title="Deliverables">
            <ul className="list-inside list-disc space-y-2">
              <li>
                <Highlight>Work Items:</Highlight> Number of tasks to complete
              </li>
              <li>
                <Highlight>Uncertainty (Min/Max):</Highlight> Potential additional scope. Leave empty if unknown, use 0
                for no uncertainty
              </li>
              <li>
                <Highlight>Due Date:</Highlight> Optional deadline—shows risk probability in results
              </li>
              <li>
                <Highlight>Dependencies:</Highlight> Work that must finish first (same team or cross-team)
              </li>
              <li>Drag to reorder within team</li>
            </ul>
          </AccordionSection>

          <AccordionSection icon={<Target className="size-5" />} title="Features">
            <p>
              Optional grouping for related deliverables across teams. Create in the Features section, then assign via
              dropdown on individual deliverables.
            </p>
          </AccordionSection>

          <AccordionSection icon={<Settings className="size-5" />} title="Simulation Settings">
            <ul className="list-inside list-disc space-y-2">
              <li>
                <Highlight>Iterations:</Highlight> 500–2000 recommended (more = accurate but slower)
              </li>
              <li>
                <Highlight>Random Seed:</Highlight> Set a value for reproducible results
              </li>
            </ul>
          </AccordionSection>

          <AccordionSection icon={<BarChart3 className="size-5" />} title="Results">
            <ul className="list-inside list-disc space-y-2">
              <li>
                <Highlight>Timeline View:</Highlight> Gantt-style chart with completion probability bands
              </li>
              <li>
                <Highlight>Details View:</Highlight> Cards showing P50/P85/P95 dates and distribution charts
              </li>
              <li>Filter by team or feature, search by name</li>
            </ul>
          </AccordionSection>

          <AccordionSection icon={<Lightbulb className="size-5" />} title="Tips for Better Forecasts">
            <ul className="list-inside list-disc space-y-2">
              <li>Use 2–4 weeks of throughput history for reliable forecasts</li>
              <li>Don't underestimate uncertainty—projects often discover 20–50% more work</li>
              <li>Export to Excel for backup and stakeholder sharing</li>
              <li>All changes auto-save. Multiple browser tabs sync in real-time.</li>
            </ul>
          </AccordionSection>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <div className="space-y-4 text-center">
        <p className="text-text-secondary">Ready to forecast your delivery?</p>
        <Link
          to="/simulations"
          className="
            inline-flex items-center gap-2 rounded-lg bg-action-primary px-8 py-4 text-lg font-semibold text-white
            shadow-md transition-colors
            hover:bg-action-primary-hover hover:shadow-glow
          "
        >
          <Rocket className="size-5" />
          Get Started
          <ArrowRight className="size-5" />
        </Link>
      </div>
    </div>
  );
}
